import FormatPrice from './mixins/format-price';
import ColorTemplate from './category-templates/option-color';
import render from '../helpers/render-template';
import PageTypes from '../helpers/page-types';
import { getQueryParamsObject, getUrlParams, objectToParams, setUrl } from '../helpers/handle-url';
import Tracker from '../helpers/tracker';
import { DeviceManager, MOBILE } from './DeviceManager';
import { HotfoilEffectBuilder } from '../builders/HotfoilEffectBuilder';
import observer from '../helpers/events';
import { getHTMLElement, getHTMLElements } from '../helpers/dom-interact';
import { FixPagePosition } from '../helpers/FixPagePosition';
import { Glossary } from '../helpers/Glossary';
import { SubstrateNotCompatibleWithRefinementsRule } from './Rules';
import { SubstrateNotCompatibleWithFramesRule } from './Rules';
import { TrackingData } from '../helpers/TrackingTypes';
import { GA4PDPTracker } from './GA4/GA4PDPTracker';

export class Options {
	static SPIRAL_BINDINGS = ['WB02', 'WB04'];

	/* *
	 * @constructor
	 * @param settings {Object}
	 * */
	constructor(settings) {
		observer(this);

		_.extend(this, settings);

		this.deviceManager = new DeviceManager();

		/*
		 * default information about product before pre-configuration is set
		 * @param product {Object} the first concrete product in array
		 * @param locale {String} set from product json or germany by default (possible values 'de_DE', 'fr_FR', 'de_CH', 'de_AT')
		 * */
		const activeProduct = _.find(
			this.json.concreteProducts,
			(product) => product.format === this.json.preConfig.format
		);

		this.data = {
			product: activeProduct || this.json.concreteProducts[0],
			locale: this.json.locale || 'de_DE',
			externalStoreCode: this.json.externalStoreCode || 'de_DE',
			hasSample: !!this.json.hasSample,
			customFormat: this.json.isAvailableForIndividualColorFormatChanges && !this.json.locale !== 'fr_FR',
		};

		this.data.optionsList = this.getValidOptions(this.data.product.productOptions);

		let preselection = _.extend(
			{},
			(this.opts.mode === PageTypes.CP && this.json.categoryPreConfig) || this.json.preConfig
		);

		this.shipmentOpt = preselection;
		if (this.opts.mode === PageTypes.PDP) {
			this.shipmentOpt = _.extend(this.shipmentOpt, {
				category: productJSON.groupIdentifier,
				hasSample: productJSON.hasSample,
				storeCode: productJSON.storeCode,
			});
		}

		this.crossSellingImgExtraOpts = {};

		if (this.opts.imageUpdater) {
			const hasTagOptionsAvailable = !!this.data.product.productOptions.tags?.length;
			const diecutForTag = (
				preselection.tag === Glossary.TAG_SKU
					? Glossary.RIBBON_AND_TAG_DIECUT
					: Glossary.NO_RIBBON_AND_TAG_DIECUT
			).slice(1);
			const normalizedDiecut = preselection.diecut ? preselection.diecut.slice(1) : '';

			_.extend(this.opts.imageUpdater.modelImgUpdater.data, {
				mode: this.opts.mode,
				color: preselection.color || '',
				wooden_block:
					(preselection.woodenBlock && preselection.woodenBlock.slice(2)) ||
					(preselection.frame && preselection.frame.slice(2)) ||
					'',
				paper: preselection.paper ? preselection.paper : '',
				outer_paper: preselection.outerPaper ? preselection.outerPaper : '',
				ribbon: preselection.ribbon ? preselection.ribbon : '',
				innerPages: preselection.innerPages ? preselection.innerPages : '',
				diecut: hasTagOptionsAvailable ? diecutForTag : normalizedDiecut,
			});
		}

		if (this.data.product.productOptions.premiumDesign) {
			preselection = { ...preselection, premiumDesign: this.data.product.productOptions.premiumDesign[0].key };
		}

		// update prices for already selected options
		if (this.opts.priceCalculator) {
			this.opts.priceCalculator.setSelectedOpt(preselection);
		}

		const substrateNotCompatibleWithRefinementsRule = new SubstrateNotCompatibleWithRefinementsRule();
		const substrateNotCompatibleWithFramesRule = new SubstrateNotCompatibleWithFramesRule();

		this.subscribe('options:refinement:changed', (e, selectedOption) => {
			substrateNotCompatibleWithRefinementsRule.apply(this.opts.el[0], selectedOption);
		});

		this.subscribe('options:format-refinement:changed', (e, selectedOption) => {
			substrateNotCompatibleWithRefinementsRule.apply(this.opts.el[0], selectedOption);
		});

		this.subscribe('options:frame:changed', (e, selectedOption) => {
			substrateNotCompatibleWithFramesRule.apply(this.opts.el[0], selectedOption);
			this.showOrHideWoodNote(selectedOption);
		});

		this.subscribe('options:format-frame:changed', (e, selectedOption) => {
			substrateNotCompatibleWithFramesRule.apply(this.opts.el[0], selectedOption);
			this.showOrHideWoodNote(selectedOption);
		});

		// set all components according to pre-configuration
		this.setInitialData(preselection);

		this.attachEvents();

		this.publish('activeOption', {
			sku: this.json.abstractSku,
			format: this.json.preConfig.format,
			color: this.json.preConfig.color,
			paper: this.json.preConfig.paper,
			innerPages: this.json.preConfig.innerPages,
			numberOfPages: this.json.preConfig.numberOfPages,
		});
	}

	getValidOptions(productOptions) {
		// Allow only options that actually exist
		const options = [
			'format',
			'quantity',
			'confi',
			'printQuality',
			'amountOfPhotos',
			'box',
			'numberOfPages',
			'innerPages',
		];

		// using slice to remove 's' letter in the end of the option name
		_.each(_.keys(productOptions), (key) => {
			if (key !== 'printQuality' && key.substring(key.length - 1) === 's') {
				options.push(key.slice(0, key.length - 1));
			} else {
				options.push(key);
			}
		});

		return options;
	}

	/*
	 * the method check pre-configuration and update components
	 * ('to Jessi' button, 'wishlist' button, price state)
	 * according to pre-config
	 *
	 * @method setInitialData
	 * @param preConfig {Object} default options of a product from the product json
	 * */
	setInitialData(preConfig) {
		const data = this.parseData(preConfig, true);
		if (typeof setUrl !== 'undefined' && this.opts.mode === PageTypes.PDP) {
			if (data.confi && data.confi.template_color !== preConfig.color) {
				setUrl(jQuery.param(_.extend(getUrlParams(), { color: data.confi.template_color })));
			}
		}

		data.img = {}; // no need to update image

		data.price = {}; // set by BE
		this.updateComponents(data);

		const frame = preConfig.frame ? this.getFrameColorByFrameSku(preConfig.frame) : Glossary.NO_FRAME_TYPE;
		this.showOrHideWoodNote(frame);
	}

	attachEvents() {
		const colorSelect = this.opts.el.find('.color-select');
		const body = jQuery('body');

		this.opts.el.find(this.opts.actionElement).each((i, el) => {
			el = jQuery(el);
			el.off(this.opts.event, this.opts.control).on(this.opts.event, this.opts.control, (e, data) => {
				this.onOptionAction(e, el, data);
			});
		});

		this.deviceManager.addEventListener('device:width:changed', () => {
			this.toggleMobileCTAs();
		});

		if (this.deviceManager.getCurrentState() === MOBILE && this.opts.mode !== PageTypes.CP) {
			// when flyout on mobile is closed it is allowed page scroll again

			colorSelect.find('.title, .mobile-title, .mobile-overlay').on('click', () => {
				this.closeColorOverlayMobile(colorSelect);
			});
		}

		this.opts.imageUpdater.modelImgUpdater.subscribe('color:changed', () => {
			if (this.opts.mode === PageTypes.CS) {
				const buttonEl = _.findWhere(this.opts.confiButton.buttons, { action: 'update' });

				if (buttonEl) {
					buttonEl.el.removeClass('disable');
				}
			}
		});

		jQuery(window).on('resize', () => {
			if (this.deviceManager.getCurrentState() === MOBILE && this.isColorOverlayMobileOpen(colorSelect)) {
				this.closeColorOverlayMobile(colorSelect, body);
			}
		});
	}

	toggleMobileCTAs() {
		if (this.opts.mode === PageTypes.CP) {
			return;
		}

		const mobileDisable = !this.data.product.mobile && DeviceManager.hasOrientation();

		_.each(this.opts.confiButton.buttons, (button) => {
			button.el[mobileDisable ? 'addClass' : 'removeClass']('hide mobile-disabled');
		});
	}

	onOptionAction(e, parent, data) {
		const el = e.currentTarget;

		if (
			el.classList.contains('is-disabled') ||
			el.classList.contains('is-disabled-because-refinement') ||
			el.classList.contains('is-disabled-because-frame')
		) {
			return;
		}

		if (
			this.data.customFormat &&
			(el.querySelector('.js-custom-option') || el.classList.contains('js-custom-option'))
		) {
			if (this.opts.mode === PageTypes.PDP && el.classList.contains('js-color-option')) {
				this.changeColorBehavior(parent);
			}

			return;
		}

		e.preventDefault();

		const currentOption = this.parseOptionValue(el);

		data = data || currentOption;

		if (_.isEmpty(data)) {
			return;
		}

		parent.find(this.opts.control).removeClass(this.opts.activeClass);

		el.classList.add(this.opts.activeClass);

		if (parent.hasClass('js-colors-list')) {
			parent.find('li.selected').removeClass('selected');
			el.parentNode.classList.add('selected');
			this.changeColorBehavior(parent);
		}
		if (Object.prototype.hasOwnProperty.call(data, 'color') && this.data.product.productOptions.ribbons) {
			this.changeSelectedRibbonAfterColorChanged(data);
		}

		if (Object.prototype.hasOwnProperty.call(data, 'tag') && data.tag === Glossary.TAG_SKU) {
			this.changeSelectedRibbonAfterColorChanged(data);
		}

		// on format change we reset the ribbon and check if we have to set one
		if (Object.prototype.hasOwnProperty.call(data, 'format')) {
			data.ribbon = null;
			this.changeSelectedRibbonAfterColorChanged(data);
		}

		/*
		 * setUrl method is used from handle-url mixin in order to keep changes in an address bar
		 * needed on PDP
		 * */
		if (typeof setUrl !== 'undefined' && this.opts.mode === PageTypes.PDP) {
			setUrl(jQuery.param(_.extend(getUrlParams(this.data.optionsList), data)));
		}
		this.updateComponents(this.parseData(data));

		if (this.opts.mode === PageTypes.CS) {
			this.publish('options:changed', data);

			const currentOptionArray = _.pairs(currentOption)[0];
			Tracker.track({
				category: TrackingData.PAGE_CROSSSELLING,
				action: `${TrackingData.ACTION_CROSSSELLING_CSBUTTON_CHANGE}_${currentOptionArray[0]}`,
				label: currentOptionArray[1],
			});
		}

		if (this.opts.mode === PageTypes.PDP) {
			this.publish('options:changed', data);

			const currentOptionArray = _.pairs(currentOption)[0];
			Tracker.track({
				category: TrackingData.PAGE_PDP,
				action: `change_${currentOptionArray[0]}`,
				label: currentOptionArray[1],
			});

			GA4PDPTracker.trackOption(currentOptionArray[0], currentOptionArray[1]);
		}
	}

	parseOptionValue(el) {
		const currentOption = {};

		if (this.opts.mode !== PageTypes.CP) {
			const elData = el.getAttribute('data-value');

			if (elData) {
				currentOption[elData.split(':')[0]] = elData.split(':')[1];
			}
		} else {
			_.each(el.attributes, (attr) => {
				if (~attr.name.indexOf('data-')) {
					currentOption[attr.name.slice(5)] = attr.value;
				}
			});
		}

		return currentOption;
	}

	changeSelectedRibbonAfterColorChanged(data) {
		const changeRibbonsDependingOnColor =
			this.opts.mode === PageTypes.PDP ||
			this.opts.mode === PageTypes.WISHLIST ||
			this.opts.mode === PageTypes.CS ||
			this.opts.mode === PageTypes.CP;

		if (!changeRibbonsDependingOnColor) {
			return;
		}

		let activeRibbon;
		let activeFormat = null;
		let activeTag = null;

		if (this.opts.mode === PageTypes.CP) {
			const preselectedRibbon = this.json.categoryPreConfig.ribbon || this.json.preConfig.ribbon;
			const availableRibbons = this.data.product.productOptions.ribbons;
			const defaultRibbon = availableRibbons && availableRibbons[0];

			activeRibbon = preselectedRibbon || (defaultRibbon && defaultRibbon.key);
			activeFormat =
				data.format ||
				this.opts.el.find(`.filter-format`).find(`.${this.opts.activeClass}`).attr('data-format');
			activeTag = this.opts.el.find(`.refinement-options`).find(`.${this.opts.activeClass}`).attr('data-tag');
		} else {
			activeRibbon = this.opts.el.find(`.ribbon-select .${this.opts.activeClass}`).attr('data-ribbon');
			activeFormat = data.format || null;
		}
		const isColorfulRibbonSelected = activeRibbon && !!~activeRibbon.indexOf(Glossary.PREFIX_COLORFUL_RIBBON);
		const colorfulRibbonForSelectedColor = this.getColorfulRibbonForSelectedColor(
			this.data.product,
			data.color
		)?.key;
		// this is the product that gets selected when changing the format
		// this.data.product is not updated yet at this point
		// only after the updateComponents call and setActiveProduct calls it's up2date
		const selectedProduct = activeFormat
			? this.json.concreteProducts.find((product) => product.format === activeFormat)
			: null;
		const tagFromUrl = getUrlParams(this.data.optionsList).tag;
		// in case of a tag, we need to set the ribbon
		// in case of no tag but the product has ribbon options we set the ribbon
		if (!activeTag) {
			activeTag = data.tag
				? data.tag
				: selectedProduct
				? selectedProduct.productOptions.tags?.[0]?.key
				: tagFromUrl || this.data.product.productOptions.tags?.[0]?.key;
		}

		if (!activeTag || activeTag === Glossary.TAG_SKU) {
			data.ribbon = colorfulRibbonForSelectedColor;
		} else if (
			this.opts.mode === PageTypes.CP &&
			colorfulRibbonForSelectedColor &&
			// don't set the ribbon on format change
			// only when the preconfig contains it
			!data.format &&
			this.json.preConfig.ribbon
		) {
			data.ribbon = colorfulRibbonForSelectedColor;
		}
		if (isColorfulRibbonSelected) {
			this.opts.el.find('.ribbon-select .selected-item').attr('data-option', colorfulRibbonForSelectedColor);
		}

		this.opts.el
			.find('.ribbon-select li[data-ribbon^="RC"]')
			.attr('data-value', `ribbon:${colorfulRibbonForSelectedColor}`)
			.data('value', `ribbon:${colorfulRibbonForSelectedColor}`)
			.attr('data-ribbon', colorfulRibbonForSelectedColor)
			.data('ribbon', colorfulRibbonForSelectedColor);
	}

	/*
	 * @method parseData
	 * @param data {Object} pre-configuration of the product (format, color, lacquer...)
	 * @return json {Object} information about components which should be updated
	 * */
	parseData(data, isFirstCall = false) {
		const json = {
			confi: {},
			wishlist: {},
			price: {},
			img: {},
			shipment: {},
		};
		const shouldPrepopulateSelects = isFirstCall && this.opts.mode !== PageTypes.CP;
		for (const key in data) {
			const valueIsNullAndNotRibbon = key !== 'ribbon' && data[key] === null;
			if (!Object.prototype.hasOwnProperty.call(data, key) || valueIsNullAndNotRibbon || data[key] === '') {
				continue;
			}

			const parsingMethod = `parse${key.charAt(0).toUpperCase() + key.slice(1)}Data`;

			let productOptions =
				this.data.product.productOptions[`${key}s`] || this.data.product.productOptions[`${key}`];

			if (key === 'amountOfPhotos') {
				productOptions = this.data.product.productOptions.amountsOfPhotos;
			}

			if (this[parsingMethod]) {
				let selectedOptionObject = _.find(productOptions, (option) => option.key === data[key]) || {};

				if (_.isEmpty(selectedOptionObject) && !_.isEmpty(productOptions) && productOptions[0].is_dynamic) {
					selectedOptionObject = { key: data[key], price: 0 };
				}
				this[parsingMethod](data, json, selectedOptionObject, shouldPrepopulateSelects);
			}
		}

		/*
		 * for product tile on category change url to product, according to changes on options
		 * */
		if (this.opts.mode === PageTypes.CP) {
			this.changeUrl(data);
		}

		return json;
	}

	parseFormatData(data, json, optionObject, shouldPrepopulateSelects) {
		// get a product from the set of products which is match to the format from pre-config
		const product = _.find(this.json.concreteProducts, (product) => product.format === data.format);

		/* if there is such product and it is different from the default which is set in constructor
		 * set all components to new values
		 * */

		if (product && !_.isEqual(product, this.data.product)) {
			const preselection = _.extend(
				{},
				(this.opts.mode === PageTypes.CP && this.json.categoryPreConfig) || this.json.preConfig
			);
			// Adjust preselection (preconfig) to reflect url params and available options
			_.extend(preselection, getUrlParams(this.data.optionsList));
			const activeColor = getUrlParams(this.data.optionsList).color || preselection.color;
			const preselectedRibbon = _.filter(
				product.productOptions.ribbons || [],
				(ribbon) => !~ribbon.key.indexOf(Glossary.PREFIX_COLORFUL_RIBBON) || ~ribbon.key.indexOf(activeColor)
			)[0];
			const preselectedTag = product.productOptions.tags && product.productOptions.tags[0];

			preselection.ribbon = preselectedRibbon && preselectedRibbon.key;
			preselection.tag = preselectedTag && preselectedTag.key;

			this.setActiveProduct(product, preselection, shouldPrepopulateSelects);
		}
	}

	parseColorData(data, json, optionObject) {
		if (_.keys(optionObject).length) {
			json.confi.template_color = optionObject.key;
			json.confi.template_color_id = optionObject.key; // duplication. tbr
			json.wishlist.template_color = optionObject.key;
			json.price.colorPrice = +optionObject.price;
			json.img.color = optionObject.key;

			if (_.keys(data).length <= 1 && this.opts.mode !== PageTypes.CP) {
				this.updateDetails(optionObject.label);
			}

			this.updateCrossSellings(data);
			this.publish('options:color:changed', optionObject.key);

			this.publish('options:format-color:changed', {
				format: this.data.product.format,
				color: optionObject.key,
			});
		}
		if (data.ribbon) {
			this.updateRibbons(data, json);
		}
	}

	parsePaperData(data, json, optionObject) {
		if (_.keys(optionObject).length) {
			json.confi.template_paper = optionObject.key;
			json.confi.template_paper_id = optionObject.key; // duplication. tbr
			json.wishlist.template_paper = optionObject.key;
			json.price.paperPrice = +optionObject.price;
			this.shipmentOpt.paper = optionObject.key;
			this.crossSellingImgExtraOpts.paper = optionObject.key;
			json.img.paper = optionObject.key;

			this.updateCrossSellings(data);
			this.publish('options:paper:changed', optionObject.key);
		}
	}

	parseDiecutData(data, json, optionObject) {
		const diecutSku = optionObject.key && optionObject.key !== Glossary.NO_DIECUT_SKU ? optionObject.key : '';

		json.confi.template_diecut = diecutSku;
		json.confi.template_diecut_id = diecutSku; // duplication. tbr
		json.wishlist.template_diecut = diecutSku;
		json.img.diecut = data.diecut;
		json.price.diecutPrice = +optionObject.price || 0;
		this.shipmentOpt.diecut = diecutSku;
		this.crossSellingImgExtraOpts.diecut = diecutSku;

		this.updateCrossSellings(data);
	}

	parseRefinementData(data, json, optionObject) {
		const lacquerSku = optionObject.key;

		json.confi.lacquer_sku = lacquerSku;
		json.wishlist.lacquer_sku = lacquerSku;
		json.img.lacquer = data.refinement;
		json.price.refinementPrice = +optionObject.price || 0;
		this.shipmentOpt.refinement = lacquerSku;
		this.crossSellingImgExtraOpts.refinement = lacquerSku;

		this.updateCrossSellings(data);

		this.publish('options:refinement:changed', optionObject.key);
	}

	parseQuantityData(data, json) {
		json.confi.quantity = data.quantity;
		json.wishlist.quantity = data.quantity;
		json.price.quantity = data.quantity;
		this.shipmentOpt.quantity = parseInt(data.quantity);
	}

	parseAmountOfPhotosData(data, json, optionObject) {
		if (_.keys(optionObject).length) {
			json.confi.amountOfPhotos = parseInt(optionObject.key.substr(2));
			json.confi.template_amount_of_photos_id = optionObject.key;
			json.confi.template_amount_of_photos = optionObject.key;
			json.wishlist.amountOfPhotos = optionObject.key;
			json.price.amountOfPhotosPrice = +optionObject.price;
			this.shipmentOpt.amountOfPhotos = optionObject.key;
		}
	}

	parseBoxData(data, json, optionObject) {
		if (_.keys(optionObject).length) {
			json.confi.template_box_id = optionObject.key;
			json.confi.template_box = optionObject.key;
			json.wishlist.template_box_id = optionObject.key;
			json.price.boxPrice = +optionObject.price;
			json.img.box = optionObject.key;
		}
	}

	parseWoodenBlockData(data, json, optionObject) {
		if (!!optionObject.key && json.confi.template_diecut !== 'D43' && !!this.data.product.productOptions.diecuts) {
			json.confi.template_diecut = Options.SPIRAL_BINDINGS.indexOf(optionObject.key) ? 'D42' : 'D41';
		}
		json.confi.wooden_block = optionObject.key || '';
		json.wishlist.wooden_block = optionObject.key || '';
		json.img.wooden_block = optionObject.key || '';
		json.price.woodenBlockPrice = +optionObject.price || 0;
	}

	parsePrintQualityData(data, json, optionObject) {
		json.confi.printing_quality = optionObject.key || '';
		json.wishlist.printing_quality = optionObject.key || '';
		this.shipmentOpt.printQuality = optionObject.key || '';
		json.price.printQualityPrice = +optionObject.price || 0;
	}

	parseFrameData(data, json, optionObject) {
		json.confi.wooden_frame = optionObject.key || '';
		json.wishlist.frame = optionObject.key || '';
		json.img.wooden_block = optionObject.key || '';
		json.price.framePrice = +optionObject.price || 0;
		this.shipmentOpt.frame = optionObject.key || 'WF00';

		this.publish('options:frame:changed', this.getFrameColorByFrameSku(optionObject.key));
	}

	getFrameColorByFrameSku(frameSku) {
		if (frameSku === undefined || frameSku === null || frameSku.length === 0) {
			return Glossary.NO_FRAME_TYPE;
		}

		return this.opts.imageUpdater.modelImgUpdater.convertWoodenBlockValue(frameSku.slice(2));
	}

	parseOuterPaperData(data, json, optionObject) {
		json.confi.outer_paper = optionObject.key || '';
		json.wishlist.outer_paper = optionObject.key || '';
		json.img.outer_paper = optionObject.key || '';
		json.price.outerPaperPrice = +optionObject.price || 0;
		this.shipmentOpt.outerPaper = optionObject.key || '';
		this.crossSellingImgExtraOpts.outerPaper = optionObject.key || '';

		this.updateCrossSellings(data);
	}

	parseRibbonData(data, json, optionObject) {
		json.confi.ribbon = optionObject.key || '';
		json.wishlist.ribbon = optionObject.key || '';
		json.img.ribbon = optionObject.key || '';
		json.price.ribbonPrice = +optionObject.price || 0;
		this.shipmentOpt.ribbon = optionObject.key || '';
		this.crossSellingImgExtraOpts.ribbon = optionObject.key || '';

		this.updateCrossSellings(data);
	}

	parseNumberOfPagesData(data, json, optionObject) {
		if (_.keys(optionObject).length) {
			json.confi.number_pages = optionObject.key || '';
			json.confi.numberOfPages = optionObject.key || '';
			json.wishlist.numberOfPages = optionObject.key;
			json.price.numberOfPagesPrice = +optionObject.price;
		}
	}

	parseInnerPagesData(data, json, optionObject) {
		if (_.keys(optionObject).length) {
			json.confi.innerPages = optionObject.key || '';
			json.wishlist.innerPages = optionObject.key;
			json.price.innerPagesPrice = +optionObject.price;

			this.publish('options:format-innerPages:changed', {
				format: this.data.product.format,
				innerPages: optionObject.key,
			});
		}
	}

	parseTagData(data, json, optionObject, shouldPrepopulateSelects) {
		const tagSku = optionObject.key && optionObject.key !== Glossary.NO_TAG_SKU ? optionObject.sku : '';
		const diecutKey =
			optionObject.diecut && optionObject.diecut !== Glossary.NO_RIBBON_AND_TAG_DIECUT ? optionObject.diecut : '';

		json.confi.tag_sku = tagSku || '';
		json.confi.tag_diecut = diecutKey;
		// FIXME KUN-690 remove field "template_diecut" later
		json.confi.template_diecut = json.confi.template_diecut || diecutKey;
		// FIXME KUN-690 remove field "template_diecut_id" later
		json.confi.template_diecut_id = json.confi.template_diecut_id || diecutKey;
		json.wishlist.template_diecut = diecutKey; // tbr
		json.price.tagPrice = +optionObject.price || 0;
		json.img.diecut = diecutKey || Glossary.NO_DIECUT_SKU;
		this.shipmentOpt.tag = optionObject.key || Glossary.NO_TAG_SKU;
		this.crossSellingImgExtraOpts.tag = diecutKey || Glossary.NO_DIECUT_SKU;

		this.updateCrossSellings(data);

		if (!shouldPrepopulateSelects) {
			this.updateRibbons(data, json);
		}
	}

	setActiveProduct(product, preselection, shouldPrepopulateSelects) {
		const featuresToBeReset = [
			'diecut',
			'refinement',
			'tag',
			'frame',
			'woodenBlock',
			'paper',
			'outerPaper',
			'ribbon',
			'numberOfPages',
		];

		this.data.product = product;

		this.data.optionsList = this.getValidOptions(this.data.product.productOptions);
		if (!this.data.product.productOptions.tags) {
			this.crossSellingImgExtraOpts.tag = '';
		}
		if (!this.data.product.productOptions.diecuts) {
			this.crossSellingImgExtraOpts.diecut = '';
		}

		this.crossSellingImgExtraOpts.paper = this.data.product.productOptions.papers
			? this.data.product.productOptions.papers[0].key
			: '';
		this.crossSellingImgExtraOpts.ribbon = preselection.ribbon;

		this.crossSellingImgExtraOpts.refinement = this.data.product.productOptions.refinements
			? this.data.product.productOptions.refinements[0].key
			: '';

		if (typeof setUrl !== 'undefined' && this.opts.mode === PageTypes.PDP) {
			setUrl(jQuery.param(_.omit(getUrlParams(this.data.optionsList), ['ribbon', 'tag'])));
		}

		this.updateColors(product);

		this.updatePricesSelect();

		if (!shouldPrepopulateSelects) {
			this.updateDetails();

			this.publish('options:format:changed', this.data.product.format);

			this.publish('options:format-color:changed', {
				format: this.data.product.format,
				color: this.opts.imageUpdater.modelImgUpdater.data.color,
			});

			this.publish('options:format-innerPages:changed', {
				format: this.data.product.format,
				innerPages: this.getSelectedOptionOrDefault('innerPages'),
			});

			this.updateCrossSellings();

			this.updateFeatures(featuresToBeReset, preselection);
			if (this.opts.mode === PageTypes.PDP) {
				// christmas fix SHOTZ-1726
				// TODO proper fix should be done in SHOP-875
				this.resetPreselection(product, preselection);
				// end fix SHOTZ-1726
				this.resetFeaturesInUrl(featuresToBeReset, preselection, product.productOptions);
			}

			if (this.data.product.productOptions.frames) {
				let selectedFrame = this.opts.imageUpdater.modelImgUpdater.getFrameForCurrentFormat(
					{ frame: this.getSelectedOptionOrDefault('frame', Glossary.NO_FRAME_SKU) },
					this.data.product.productOptions.frames
				);
				this.publish('options:format-frame:changed', selectedFrame);
			}

			this.publish(
				'options:format-refinement:changed',
				this.getSelectedOptionOrDefault('lacquer', Glossary.NO_REFINEMENT_SKU)
			);

			this.updateIsNewFlag(product);
		}

		this.attachEvents();

		const componentsToUpdate = ['confiButton', 'wishlistButton', 'imageUpdater', 'priceCalculator'];

		_.each(componentsToUpdate, (componentName) => {
			if (this.opts[componentName]) {
				this.opts[componentName].setActiveProduct(product, preselection);
			}
		});

		this.updatePricesSelect();

		if (this.opts.flyout && this.opts.flyout.data) {
			this.opts.flyout.data.product = product;
		}

		if (this.opts.shipmentTime) {
			this.updateShipmentOptions(product);

			this.opts.shipmentTime.calcShippingDay(this.shipmentOpt);
		}
	}

	getSelectedOptionOrDefault(type, defaultValue) {
		const dropdown = this.opts.el[0].querySelector('.' + type + '-select');

		if (dropdown && !dropdown.classList.contains('hide')) {
			const selectedOption = dropdown.querySelector(`.selected`);
			if (selectedOption) {
				return selectedOption.getAttribute('data-' + type);
			}
		}

		return defaultValue;
	}

	updateShipmentOptions(product) {
		this.shipmentOpt.format = product.format;

		this.shipmentOpt.diecut = product.productOptions.diecuts ? product.productOptions.diecuts[0].key : '';

		this.shipmentOpt.refinement = product.productOptions.refinements
			? product.productOptions.refinements[0].key
			: '';

		this.shipmentOpt.tag = product.productOptions.tags ? product.productOptions.tags[0].key : '';

		this.shipmentOpt.frame = product.productOptions.frames ? product.productOptions.frames[0].key : '';
	}

	updateFeatures(features, preselection) {
		if (this.opts.mode === PageTypes.CP) {
			_.each(features, (feature) => {
				this.opts.el.find(`.${feature}-options ul`).empty();
			});
		}

		const urlParams = getUrlParams(this.data.optionsList);

		/* eslint-disable complexity */
		_.each(features, (feature) => {
			let currentFeature = this.data.product.productOptions[`${feature}s`];

			switch (feature) {
				case 'innerPages':
				case 'numberOfPages':
					currentFeature = this.data.product.productOptions[feature];
					break;
			}
			const data = _.filter(currentFeature, (obj) => obj.key !== 'D60');
			const element = this.opts.el.find(`.${feature}${this.opts.mode !== PageTypes.CP ? '-select' : '-options'}`);

			if (!element.length) {
				return;
			}

			const amountOfOptions = data.length;

			element.addClass('hide');

			if (this.opts.mode !== PageTypes.CP && feature !== 'paper') {
				element.find('li:gt(0)').remove();
			} else if (this.opts.mode !== PageTypes.CP && feature === 'paper' && amountOfOptions > 0) {
				element.find('li').remove();

				const defaultOption = data[0];
				const sign = defaultOption.price > 0 ? ' +' : ' ';
				const iconHtml = '<strong class="icon empty"></strong>';
				const priceFormatted = FormatPrice.format(
					this.opts.priceCalculator.calculateFinalPrice(defaultOption.price),
					this.data.locale
				);
				const priceFormattedWithSign = defaultOption.price ? `${sign}${priceFormatted}` : '';
				const optionLabel = `${defaultOption.label}${priceFormattedWithSign}`;
				const itemValue = `${iconHtml}<em class="name">${optionLabel}</em>`;

				element.find('.selected-item').html(itemValue);
			}
			const optionsHTML = this.generateNewOptionsHTML(data, feature, urlParams, preselection);

			if (this.isFeatureShown(amountOfOptions, feature, urlParams)) {
				const ul = element.find('ul');

				element.removeClass('hide');

				if (this.opts.mode === PageTypes.CP) {
					ul.append(optionsHTML);
				} else {
					ul.html(optionsHTML);
				}
			}

			if (this.opts.mode !== PageTypes.CP && feature !== 'paper') {
				const selectedElementOrDefault = element.find('li.selected').first().length
					? element.find('li.selected').first()
					: element.find('li:first');
				element.find('.selected-item').html(selectedElementOrDefault.html()); // reset to default
			}

			this.updateFeatureClassNames(element, amountOfOptions);
		});
		/* eslint-enable complexity */
	}

	updateFeatureClassNames(element, amountOfOptions) {
		if (amountOfOptions > 1) {
			element.removeClass('one-option').find('.select-options').removeClass('hide');
		} else if (amountOfOptions === 1) {
			element.addClass('one-option').find('.select-options').addClass('hide');
		}
	}

	isFeatureShown(amountOfOptions, feature, urlParams) {
		// do not show ribbon select if there is tag and it is not selected

		if (feature === 'ribbon') {
			const isTagAvailable = !!this.data.product.productOptions.tags?.length;
			const tagSku = urlParams.tag ?? this.json.preConfig.tag;
			const isTagSelected = tagSku && tagSku !== Glossary.NO_TAG_SKU;

			if (isTagAvailable && !isTagSelected) {
				return false;
			}
		}

		return this.isFeatureSingleSpiralBinding(feature, amountOfOptions, this.data.product.productOptions)
			? false
			: amountOfOptions > 1 ||
					(!!~[
						'format',
						'refinement',
						'paper',
						'woodenBlock',
						'ribbon',
						'numberOfPages',
						'innerPages',
					].indexOf(feature) &&
						amountOfOptions > 0) ||
					(feature === 'tag' &&
						!!this.data.product.productOptions.refinements &&
						this.opts.mode === PageTypes.CP) ||
					(feature === 'refinement' &&
						!!this.data.product.productOptions.tags &&
						this.opts.mode === PageTypes.CP);
	}

	isFeatureSingleSpiralBinding(feature, amountOfOptions, productOptions) {
		return (
			feature === 'woodenBlock' &&
			amountOfOptions === 1 &&
			Options.SPIRAL_BINDINGS.indexOf(productOptions.woodenBlocks[0].key)
		);
	}

	generateNewOptionsHTML(data, feature, urlParams, preselection) {
		const activeColor = urlParams.color || this.json.preConfig.color;

		let html = '';

		let optionIndex = -1;
		const allEqual = data.every((value, i, array) => value.price === array[0].price);

		if (feature === 'ribbon') {
			const containsMatchingColorRibbon = data.some((ribbon) => ribbon.key.includes(activeColor));
			data = data.filter((ribbon) => {
				const isColorfulRibbon = ribbon.key.includes(Glossary.PREFIX_COLORFUL_RIBBON);
				if (ribbon.key.includes(activeColor)) {
					return true;
				} else if (isColorfulRibbon && !containsMatchingColorRibbon && ribbon.key.includes('C01')) {
					return true;
				} else if (isColorfulRibbon) {
					return false;
				}
				return true;
			});
		}
		let preselectedFeatureValue = urlParams[feature] || this.json.preConfig[feature];
		if (feature === 'refinement') {
			preselectedFeatureValue = this.getRefinementFromUrlOrPreselection(preselection, data, urlParams);
		}

		const preselectedValueIndexOrDefault = Math.max(
			_.findIndex(data, (obj) => obj.key === preselectedFeatureValue),
			0
		);

		_.each(data, (obj) => {
			if (this.opts.mode !== PageTypes.CP) {
				let optionLabel = obj.label;

				if (!allEqual && obj.price) {
					const sign = obj.price > 0 ? ' +' : ' ';
					const finalPrice = FormatPrice.format(
						this.opts.priceCalculator.calculateFinalPrice(obj.price),
						this.data.locale
					);

					optionLabel += sign + finalPrice;
				}
				let iconHtml = '<strong class="icon empty"></strong>';

				if (feature === 'woodenBlock') {
					iconHtml = `<strong class="with-icon icon without-text svg-format ${obj.key}-${obj.type}" title="${obj.key}">${obj.key}</strong>`;
				}
				if ((feature === 'refinement' || feature === 'paper') && data.length === 1) {
					optionLabel = obj.label;
				}

				let liValue = `${iconHtml}<em class="name">${optionLabel}</em>`;

				if (feature === 'paper' && optionIndex > -1) {
					liValue += getHTMLElement(document, '#js-incompatible-substrate-with-options-messages').innerHTML;
				}

				optionIndex++;

				html += `<li data-value="${feature}:${obj.key}" data-${feature}="${obj.key}" data-price="${
					obj.price
				}" class="option${optionIndex === preselectedValueIndexOrDefault ? ' selected' : ''}">${liValue}</li>`;
			} else if (!~[Glossary.NO_DIECUT_SKU, Glossary.NO_REFINEMENT_SKU, Glossary.NO_TAG_SKU].indexOf(obj.key)) {
				html += `<li class="with-icon without-text svg-${feature} ${obj.key.toLowerCase()} option${
					urlParams[feature] === obj.key ? ' active' : ''
				}" data-${feature}="${obj.key}"></li>`;
			}
		});

		return html;
	}

	getColorfulRibbonForSelectedColor(product, color) {
		const queryActiveColorCP = `.filter-colors .${this.opts.activeClass}.color-item`;
		const queryActiveColorNonCP = `.colors-list .${this.opts.activeClass} .color-item`;
		const queryActiveColor = this.opts.mode === PageTypes.CP ? queryActiveColorCP : queryActiveColorNonCP;

		const selectedColor = color || this.opts.el.find(queryActiveColor).attr('data-color');
		const colorfulRibbons = _.filter(
			product.productOptions.ribbons,
			(item) => !!~item.key.indexOf(Glossary.PREFIX_COLORFUL_RIBBON)
		);
		const whiteRibbon = _.find(colorfulRibbons, (item) => !!~item.key.indexOf('C01'));
		const firstRibbon = _.first(product.productOptions.ribbons);
		const defaultRibbon = whiteRibbon || firstRibbon;

		const colorfulRibbonKey =
			product.mappingCardColorToRibbonSkus[selectedColor] || (defaultRibbon && defaultRibbon.key) || null;

		return _.find(colorfulRibbons, (item) => item.key === colorfulRibbonKey) || defaultRibbon;
	}

	// christmas fix SHOTZ-1726
	// TODO proper fix should be done in SHOP-875
	resetPreselection(product, preselection) {
		preselection.woodenBlock =
			(product.productOptions.woodenBlocks &&
				product.productOptions.woodenBlocks[0] &&
				product.productOptions.woodenBlocks[0].key) ||
			preselection.woodenBlock;

		preselection.frame =
			(product.productOptions.frames &&
				product.productOptions.frames[0] &&
				product.productOptions.frames[0].key) ||
			preselection.frame;

		const preselectionRefinementOrDefault =
			product.productOptions.refinements?.find((refinement) => refinement.key === preselection.refinement)?.key ??
			(product.productOptions.refinements &&
				product.productOptions.refinements[0] &&
				product.productOptions.refinements[0].key);

		preselection.refinement = preselectionRefinementOrDefault ? preselectionRefinementOrDefault : undefined;
	}

	// end fix SHOTZ-1726
	/*
	 * when a format is changed the set of colors should be updated accordingly
	 * @method updateColors
	 * @param product {Object} information about concrete product
	 * */
	updateColors(product) {
		const element = this.opts.el[0];

		if (this.opts.mode !== PageTypes.CP) {
			const colorWrapper = getHTMLElement(element, '.js-colors-list');
			const colors = getHTMLElements(colorWrapper, '.js-color-item');

			_.each(colors, (colorItem) => {
				const colorValue = getHTMLElement(colorItem, '.js-color-option').getAttribute('data-color');
				const isAvailable =
					!colorValue ||
					_.find(product.productOptions.color, (colorObject) => colorObject.key === colorValue);

				colorItem.classList[isAvailable ? 'remove' : 'add']('hide');
			});

			if (colorWrapper.querySelector('.js-color-item.selected.hide')) {
				getHTMLElement(colorWrapper, '.js-color-item:not(.hide) .js-color-option').click();
			}
		} else {
			let selectedColor = '';
			try {
				// Workaround for https://kartenmacherei.atlassian.net/browse/QARM-249?focusedCommentId=196542
				const selectedColorElement = getHTMLElement(element, `.filter-colors .${this.opts.activeClass}`);
				selectedColor = selectedColorElement.getAttribute('data-color');
			} catch (e) {
				selectedColor = product.productOptions.color[0].key;
			}

			const filterTitleElement = element.querySelector('.filter-colors .js-custom-option')
				? getHTMLElement(element, '.filter-colors .js-custom-option').getAttribute('title')
				: getHTMLElement(element, '.filter-colors').getAttribute('title');

			const customOptionTitle = this.data.customFormat && filterTitleElement;

			getHTMLElement(element, '.filter-colors').innerHTML = render(ColorTemplate, {
				colors: product.productOptions.color,
				selected: selectedColor,
				customOptionTitle,
			});
		}
	}

	/*
	 * update url to product on product tile, if default options of product were changed
	 * @method changeUrl
	 * @param data {Object} pre-configuration of the product (format, color, lacquer...)
	 * */
	changeUrl(data) {
		const link = this.opts.el.find(this.opts.toProductLink);
		const linkHref = link.attr('href').split('?');

		let params = _.extend({}, getQueryParamsObject(linkHref[1]), data);

		let preselection = _.extend(
			{},
			(this.opts.mode === PageTypes.CP && this.json.categoryPreConfig) || this.json.preConfig
		);

		// ignore params if they're default (as in preConfig)
		_.each(params, (val, key) => {
			if (key === 'frame') {
				if (
					this.opts.imageUpdater &&
					this.data.product &&
					this.data.product.productOptions &&
					this.data.product.productOptions.frames
				) {
					const currentFrame = this.opts.imageUpdater.modelImgUpdater.getFrameForCurrentFormat(
						preselection,
						this.data.product.productOptions.frames
					);

					if (currentFrame) {
						params.frame = currentFrame;
					} else {
						params = _.omit(params, key);
					}
				}
			} else {
				if (this.json.preConfig[key] === val) {
					params = _.omit(params, key);
				}
			}
		});

		const createdLink = linkHref[0] + (_.keys(params).length ? `?${jQuery.param(params)}` : '');
		link.attr('href', createdLink);
	}

	changeColorBehavior(parent) {
		const SUPPORTED_THEMES = ['kam', 'kartenmacherei-rebrush'];
		if (this.deviceManager.getCurrentState() !== MOBILE || !SUPPORTED_THEMES.includes(window.frontendTheme)) {
			return;
		}

		const colorsHolder = parent.parents('.color-select');

		// when the flyout with colors is opened on the mobile view this class prevent of scrolling the page in background and vise versa
		if (this.isColorOverlayMobileOpen(colorsHolder)) {
			this.closeColorOverlayMobile(colorsHolder);
		} else {
			colorsHolder.addClass('open');

			if (this.forbidPageScroll) {
				FixPagePosition.getInstance().registerComponent(this);
			}

			colorsHolder.addClass('opened-on-mobile').removeClass('closed-on-mobile');
		}
	}

	closeColorOverlayMobile(colorOverlay) {
		colorOverlay.removeClass('opened-on-mobile').addClass('closed-on-mobile');

		if (this.deviceManager.getCurrentState() === MOBILE && this.forbidPageScroll) {
			FixPagePosition.getInstance().unregisterComponent(this);
		}
	}

	isColorOverlayMobileOpen(colorSelect) {
		return colorSelect.hasClass('opened-on-mobile');
	}

	updateRibbons(data, json) {
		const ribbons = this.data.product.productOptions.ribbons || [];

		if (this.opts.mode !== PageTypes.PDP || !ribbons.length) {
			return;
		}

		const urlParams = getUrlParams(this.data.optionsList);
		const activeColor = urlParams.color || this.json.preConfig.color;

		let activeRibbon = _.find(ribbons, (ribbon) => ribbon.key === data.ribbon) || {};

		// We should only look for the corresponding colorful ribbon
		// if the selected ribbon is color-specific
		if (data.tag !== Glossary.NO_TAG_SKU && data.ribbon && data.ribbon.includes(Glossary.PREFIX_COLORFUL_RIBBON)) {
			activeRibbon = _.find(
				ribbons,
				(ribbon) => !~ribbon.key.indexOf(Glossary.PREFIX_COLORFUL_RIBBON) || ~ribbon.key.indexOf(activeColor)
			);
			if (!activeRibbon) {
				activeRibbon = _.find(
					ribbons,
					(ribbon) => ribbon.key.includes(Glossary.PREFIX_COLORFUL_RIBBON) && ribbon.key.includes('C01')
				);
			}
		}

		if (!data.ribbon) {
			data.ribbon = activeRibbon.key;
		}
		this.updateFeatures(['ribbon']);

		if (!data.ribbon && urlParams.ribbon) {
			setUrl(jQuery.param(_.omit(urlParams, 'ribbon')));
		}

		this.parseRibbonData(data, json, activeRibbon);
	}

	/**
	 *
	 * @param data is optional
	 */
	updateCrossSellings(data) {
		if (!this.opts.crossSellingsHolder) {
			return;
		}

		this.opts.crossSellingsHolder.empty();

		let CShtml = '';
		const design = this.json.abstractSku;
		const { format } = this.data.product;
		const color = data && data.color ? data.color : this.opts.imageUpdater.modelImgUpdater.data.color;
		const diecut = this.crossSellingImgExtraOpts.diecut || this.crossSellingImgExtraOpts.tag || '';
		const refinement = this.crossSellingImgExtraOpts.refinement || '';
		const paper = this.crossSellingImgExtraOpts.paper || '';
		const outerPaper = this.crossSellingImgExtraOpts.outerPaper || '';
		const ribbon = this.crossSellingImgExtraOpts.ribbon || '';

		let CSurl;

		_.each(this.data.product.crossSellings || [], (csGroup) => {
			_.each(csGroup.products || [], (cs) => {
				CSurl = `${this.json.customizedImageUrl}?template=${cs.imageTemplate}&amp;design=${design}&amp;format=${format}&amp;color=${color}&amp;diecut=${diecut}&amp;lacquer=${refinement}&amp;locale=${this.data.externalStoreCode}&amp;category=${cs.category}&amp;paper=${paper}&amp;outerpaper=${outerPaper}&amp;ribbon=${ribbon}`;

				CShtml += `<li><img class="product-img" src="${CSurl}" alt="${csGroup.title}"><span class="name">${cs.title}</span></li>`;
			});
		});

		this.opts.crossSellingsHolder.html(CShtml);

		if (this.opts.imageUpdater) {
			this.opts.imageUpdater.crossSellingImg = this.opts.crossSellingsHolder.find('img');
		}
	}

	updatePricesSelect() {
		const { priceCalculator } = this.opts;
		const { prices } = this.data.product;
		const pricesSelect = this.opts.el.find('.prices-select');

		if (pricesSelect.length === 0) {
			return;
		}
		let quantity = this.data.product.defaultQuantity;

		if (this.json.preConfig && this.json.preConfig.quantity) {
			quantity = this.json.preConfig.quantity;
		}

		prices.forEach((priceObj) => {
			const item = pricesSelect.find(`li[data-quantity="${priceObj.quantity}"]`);

			if (item.hasClass(this.opts.activeClass)) {
				quantity = item.data('quantity');
			}

			if (priceObj.isSample) {
				return;
			}

			const totalPrice = priceObj.price + priceCalculator.getExtras();
			const finalPrice = priceCalculator.calculateFinalPrice(totalPrice);

			item.find('.val-holder').html(FormatPrice.format(finalPrice, this.data.locale));
		});

		const selectedQuantityNodes = document.getElementsByClassName('js-selected-quantity');

		if (selectedQuantityNodes.length > 0) {
			selectedQuantityNodes[0].textContent = quantity;
		}

		const curPrice = prices.find((priceObj) => +priceObj.quantity === +quantity);

		if (!curPrice.isSample && !curPrice.isSampleFree) {
			const totalPrice = curPrice.price + priceCalculator.getExtras();
			const finalPrice = priceCalculator.calculateFinalPrice(totalPrice);
			const formattedPrice = FormatPrice.format(finalPrice, this.data.locale);

			pricesSelect.find('.selected-item .val-holder').html(formattedPrice);
		}
	}

	updateDetails(color) {
		if (!this.opts.detailsHolder) {
			return;
		}

		this.opts.detailsHolder.find('.format-label').html(this.data.product.formatLabel);

		const dimensionDetailsHolder = this.opts.detailsHolder.find('.dimensions');

		if (this.data.product.dimensionLong) {
			dimensionDetailsHolder.html(this.data.product.dimensionLong);
		} else {
			dimensionDetailsHolder.html(this.data.product.dimension);
		}

		this.showOrHideAssemblyNode();
		this.showOrHideAdditionalNode();
		this.showOrHideSealNote();

		this.opts.detailsHolder
			.find('.postage')
			.html(FormatPrice.format((this.data.product.postage / 100).toFixed(2), this.data.locale));

		if (color) {
			this.opts.detailsHolder.find('.color').html(color);
		}
	}

	showOrHideWoodNote(currentFrame) {
		if (this.opts.mode === PageTypes.PDP && this.opts.detailsHolder) {
			const woodNoteTextGeneral = this.opts.detailsHolder.find('.js-wood-note-text-general');

			if (woodNoteTextGeneral) {
				if (currentFrame === Glossary.NO_FRAME_TYPE) {
					woodNoteTextGeneral.hide();
				} else {
					woodNoteTextGeneral.show();
				}
			}
		}
	}

	showOrHideAssemblyNode() {
		const assemblyNoteLabel = this.opts.detailsHolder.find('.js-assembly-note-label');
		const assemblyNoteText = this.opts.detailsHolder.find('.js-assembly-note-text');
		const assemblyNoteTextGeneral = this.opts.detailsHolder.find('.js-assembly-note-text-general');

		if (this.data.product.showAssemblyNote) {
			let hideGeneralAssemblyNote = false;

			assemblyNoteLabel.show();
			assemblyNoteText.each((i, el) => {
				el = jQuery(el);
				const assemblyNoteFormat = el.data('format');

				if (this.data.product.format !== assemblyNoteFormat) {
					el.hide();
				} else {
					el.show();
					hideGeneralAssemblyNote = true;
				}
			});
			if (hideGeneralAssemblyNote) {
				assemblyNoteTextGeneral.hide();
			}
		} else {
			assemblyNoteLabel.hide();
			assemblyNoteText.hide();
			assemblyNoteTextGeneral.hide();
		}
	}

	showOrHideAdditionalNode() {
		const additionalNoteLabel = this.opts.detailsHolder.find('.js-additional-note-label');
		const additionalNoteText = this.opts.detailsHolder.find('.js-additional-note-text');

		let showAdditionalNode = false;

		additionalNoteText.each((i, el) => {
			el = jQuery(el);
			const additionalNoteFormat = el.data('format');

			if (this.data.product.format !== additionalNoteFormat) {
				el.hide();
			} else {
				showAdditionalNode = true;
				el.show();
			}
		});

		if (showAdditionalNode) {
			additionalNoteLabel.show();
		} else {
			additionalNoteLabel.hide();
		}
	}

	showOrHideSealNote() {
		const sealNoteHolder = this.opts.detailsHolder.find('.js-seal-note-holder');

		if (this.data.product.hasImplicitSeal) {
			sealNoteHolder.show();
		} else {
			sealNoteHolder.hide();
		}
	}

	updateComponents(data) {
		if (this.opts.openClose && this.data.product.jessi && data.img) {
			this.opts.openClose.update(data.img);
		}

		if (this.opts.confiButton) {
			this.opts.confiButton.update(data.confi);
		}

		if (this.opts.wishlistButton) {
			this.opts.wishlistButton.updateDataFromProductJson(data.wishlist);
		}

		if (this.opts.priceCalculator && Object.keys(data.price).length !== 0) {
			if (this.opts.priceCalculator.update(data.price)) {
				this.updatePricesSelect();
			}
		}

		if (this.opts.imageUpdater && Object.keys(data.img).length !== 0) {
			this.opts.imageUpdater.setNewSrc(data.img);
		}

		if (this.opts.shipmentTime) {
			this.opts.shipmentTime.calcShippingDay(this.shipmentOpt);
		}

		const refinement = this.opts.imageUpdater.modelImgUpdater.data.lacquer;

		if (refinement) {
			this.updateHotfoil(`V${refinement}`);
		}

		this.toggleMobileCTAs();
	}

	updateHotfoil(data) {
		if (this.opts.mode !== PageTypes.PDP && this.opts.mode !== PageTypes.CP) {
			return;
		}

		const hotfoil = HotfoilEffectBuilder.getHotfoil(data);

		this.opts.hotfoilBuilder.hotfoil = hotfoil;

		if (!hotfoil) {
			return;
		}

		if (this.opts.mode === PageTypes.PDP && this.opts.hotfoilEffects.desktop) {
			this.opts.hotfoilEffects.desktop.filter(Boolean).forEach((effect) => effect.update(hotfoil.cssClass));
		}
		if (this.opts.mode === PageTypes.PDP && this.opts.hotfoilEffects.mobile) {
			this.opts.hotfoilEffects.mobile.filter(Boolean).forEach((effect) => effect.update(hotfoil.cssClass));
		}

		if (this.opts.mode === PageTypes.CP && this.opts.hotfoilEffect) {
			this.opts.hotfoilEffect.update(hotfoil.cssClass);
		}
	}

	updateIsNewFlag(data) {
		if (this.opts.mode === PageTypes.CP) {
			const element = this.opts.el.find('.label-new');

			element[data.isNew ? 'removeClass' : 'addClass']('is-hidden');
		}
	}

	resetFeaturesInUrl(features, preselection, productOptions) {
		const urlParams = getUrlParams();
		features.forEach((feature) => {
			if (urlParams[feature]) {
				delete urlParams[feature];
			}
		});

		// we don't reset the refinement to keep the selected lacquer in the url if there was a selected one
		if (preselection.refinement) {
			urlParams.refinement = preselection.refinement;
		}

		setUrl(objectToParams(urlParams));
	}

	getRefinementFromUrlOrPreselection(preselection, refinementOptions, urlParams) {
		const selectedRefinement = urlParams.refinement || '';
		// keep refinement if it exists for selected format
		if (
			refinementOptions &&
			refinementOptions.length > 0 &&
			refinementOptions.find((r) => r.key === selectedRefinement)
		) {
			return selectedRefinement;
			// otherwise return preselected refinement if it exists for selected format
		} else if (preselection && preselection.refinement && preselection.refinement !== '') {
			return preselection.refinement;
		}
		return undefined;
	}
}
